import React, { useState, useEffect, useRef } from 'react'
import { withTranslation } from 'react-i18next'
import {
  Box,
  Alert,
  Typography,
  TextField,
  Button,
  Grid,
  Link,
  Checkbox,
  FormControlLabel,
  Paper
} from '@mui/material'

import { useMutation, gql } from '@apollo/client'
import { ownerLogin } from '../apollo'
import { validateFunc } from '../constraints/constraints'
import Logo from '../assets/img/rushmyfood-logo.png'

const LOGIN = gql`${ownerLogin}`

const Login = props => {
  const [showPassword, setShowPassword] = useState(false)
  const [stateData, setStateData] = useState({
    email: '',
    password: '',
    emailError: null,
    passwordError: null,
    error: null,
    type: null,
    redirectToReferrer: !!localStorage.getItem('user-enatega')
  })
  const formRef = useRef()
  const { t } = props

  const [isLogged, setIsLogged] = useState(false)

  const onBlur = (event, field) => {
    setStateData({
      ...stateData,
      [field + 'Error']: !validateFunc({ [field]: stateData[field] }, field)
    })
  }

  const validate = () => {
    const emailError = !validateFunc({ email: stateData.email }, 'email')
    const passwordError = !validateFunc(
      { password: stateData.password },
      'password'
    )
    setStateData({ ...stateData, emailError, passwordError })
    return emailError && passwordError
  }

  const { redirectToReferrer, type } = stateData

  useEffect(() => {
    if (isLogged) {
      if (redirectToReferrer && type === 0) {
        props.history.replace('/restaurant/home')
      }
      if (redirectToReferrer && type === 1) {
        props.history.replace('/super_admin/vendors')
      }
      if (redirectToReferrer && type === 2) {
        props.history.replace('/agent/home')
      }
    }
  }, [isLogged, redirectToReferrer, type, props.history])

  const onCompleted = data => {
    localStorage.setItem('user-enatega', JSON.stringify(data.ownerLogin))
    const userType = data.ownerLogin.userType

    // Store vendorId so Restaurant.jsx can load this vendor's restaurants
    localStorage.setItem('vendorId', data.ownerLogin.userId)
    // Clear any stale vendorFilterId from super-admin navigation
    localStorage.removeItem('vendorFilterId')

    // If user has restaurants, set the first restaurant ID in localStorage
    if (data.ownerLogin.restaurants && data.ownerLogin.restaurants.length > 0) {
      const firstRestaurant = data.ownerLogin.restaurants[0]
      localStorage.setItem('restaurantId', firstRestaurant._id)
      localStorage.setItem('restaurantName', firstRestaurant.name)
      localStorage.setItem('restaurantImage', firstRestaurant.image)
    } else {
      // Super admin has no restaurants — clear any stale vendor restaurant data
      localStorage.removeItem('restaurantId')
      localStorage.removeItem('restaurantName')
      localStorage.removeItem('restaurantImage')
    }

    if (userType === 'AGENT') {
      setStateData({
        ...stateData,
        redirectToReferrer: true,
        type: 2,
        emailError: null,
        passwordError: null
      })
    } else if (userType === 'VENDOR') {
      setStateData({
        ...stateData,
        redirectToReferrer: true,
        type: 0,
        emailError: null,
        passwordError: null
      })
    } else {
      setStateData({
        ...stateData,
        redirectToReferrer: true,
        type: 1,
        emailError: null,
        passwordError: null
      })
    }
    setIsLogged(true)
    setTimeout(hideAlert, 5000)
  }

  const hideAlert = () => {
    setStateData({
      ...stateData,
      emailError: null,
      passwordError: null
    })
  }

  const onError = error => {
    if (error.graphQLErrors.length) {
      setStateData({
        ...stateData,
        error: error.graphQLErrors[0].message
      })
    }
    if (error.networkError) {
      setStateData({
        ...stateData,
        error: error.message
      })
    }
    setIsLogged(false)
    setTimeout(hideAlert, 5000)
  }

  const [mutate] = useMutation(LOGIN, { onCompleted, errorPolicy: 'none' })

  const loginFunc = async() => {
    const email = stateData.email.trim().toLowerCase()
    const password = stateData.password
    if (!email || !password) {
      setStateData({ ...stateData, error: 'Please enter email and password' })
      return
    }
    try {
      await mutate({ variables: { email, password } })
    } catch (err) {
      setStateData({ ...stateData, error: err.graphQLErrors?.[0]?.message || err.message || 'Invalid email or password' })
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      loginFunc()
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 2
      }}
    >
      <Paper
        elevation={24}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          borderRadius: '20px',
          overflow: 'hidden',
          maxWidth: 900,
          width: '100%',
          minHeight: { xs: 'auto', md: 500 }
        }}
      >
        {/* Left Side - Branding */}
        <Box
          sx={{
            flex: 1,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: { xs: 3, md: 4 },
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
              backgroundSize: '20px 20px'
            }
          }}
        >
          {/* Logo Card */}
          <Paper
            elevation={8}
            sx={{
              padding: { xs: 2.5, md: 4 },
              borderRadius: '16px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: { xs: 2, md: 4 },
              backgroundColor: '#fff'
            }}
          >
            <img
              src={Logo}
              alt="RushmyFood Logo"
              style={{
                width: 100,
                maxWidth: '30vw',
                height: 'auto',
                marginBottom: 8
              }}
            />
          </Paper>

          {/* Welcome Text */}
          <Typography
            variant="h4"
            sx={{
              color: '#fff',
              fontWeight: 700,
              textAlign: 'center',
              fontSize: { xs: '1.6rem', md: '2.125rem' },
              mb: { xs: 1, md: 2 },
              position: 'relative',
              zIndex: 1
            }}
          >
            Welcome Back!
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.8)',
              textAlign: 'center',
              maxWidth: 280,
              fontSize: { xs: '0.85rem', md: '1rem' },
              position: 'relative',
              zIndex: 1
            }}
          >
            Admin Portal for managing your food delivery operations
          </Typography>
        </Box>

        {/* Right Side - Login Form */}
        <Box
          sx={{
            flex: 1,
            padding: { xs: 3, md: 5 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            backgroundColor: '#fff'
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#1a1a2e',
              mb: 1
            }}
          >
            Admin Login
          </Typography>
          <Typography
            sx={{
              color: '#666',
              mb: 4
            }}
          >
            Enter your credentials to access the portal
          </Typography>

          <form ref={formRef} onKeyPress={handleKeyPress}>
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 600, mb: 1, color: '#1a1a2e' }}>
                Email Address
              </Typography>
              <TextField
                fullWidth
                value={stateData.email}
                onChange={e => setStateData({ ...stateData, email: e.target.value })}
                onBlur={e => onBlur(e, 'email')}
                placeholder="Enter your email"
                variant="outlined"
                error={stateData.emailError === false}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: 'rgba(102, 126, 234, 0.1)',
                    '& fieldset': {
                      borderColor: 'transparent'
                    },
                    '&:hover fieldset': {
                      borderColor: '#667eea'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea'
                    }
                  },
                  '& .MuiOutlinedInput-input': {
                    color: '#1a1a2e'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#888',
                    opacity: 1
                  }
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 600, mb: 1, color: '#1a1a2e' }}>
                Password
              </Typography>
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                value={stateData.password}
                onChange={e => setStateData({ ...stateData, password: e.target.value })}
                onBlur={e => onBlur(e, 'password')}
                placeholder="Enter your password"
                variant="outlined"
                error={stateData.passwordError === false}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: 'rgba(102, 126, 234, 0.1)',
                    '& fieldset': {
                      borderColor: 'transparent'
                    },
                    '&:hover fieldset': {
                      borderColor: '#667eea'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea'
                    }
                  },
                  '& .MuiOutlinedInput-input': {
                    color: '#1a1a2e'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#888',
                    opacity: 1
                  }
                }}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{
                      color: '#667eea',
                      '&.Mui-checked': {
                        color: '#667eea'
                      }
                    }}
                  />
                }
                label={<Typography sx={{ color: '#666' }}>Remember me</Typography>}
              />
              <Link
                href="/#/auth/reset"
                sx={{
                  color: '#667eea',
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Forgot Password?
              </Link>
            </Box>

            <Button
              fullWidth
              type="button"
              onClick={loginFunc}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                padding: '14px',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)'
                }
              }}
            >
              Sign In
            </Button>
          </form>

          {stateData.error && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: '10px' }}>
              {stateData.error}
            </Alert>
          )}

          <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid #f1f5f9', textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8' }}>
              Restaurant staff?{' '}
              <Link
                href="/#/auth/staff-login"
                sx={{ color: '#667eea', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                Staff Login
              </Link>
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

export default withTranslation()(Login)
